import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { PAGE_ZOOM_CHANGED_EVENT } from '../../utils/pageZoom';
import { QUESTION_TYPE_LABEL } from '../../utils/remoteQuestion';
import type { RemoteNoticeView, RemoteQuestionView } from './RemotePresentationOverlays';
import { useRemoteBacklogNavState } from '../../utils/remoteBacklogStore';
import './RemoteMessageBacklog.css';

/** 아직 확인하지 않은 공지/질문 (탭별 sessionStorage 보관) */
export type RemoteBacklogItem =
  | { kind: 'notice'; id: string; receivedAt: number; seen: boolean; confirmed?: boolean; notice: RemoteNoticeView }
  | {
      kind: 'question';
      id: string;
      receivedAt: number;
      seen: boolean;
      confirmed?: boolean;
      answered?: boolean;
      question: RemoteQuestionView;
    }
  | { kind: 'caption'; id: string; receivedAt: number; seen: boolean; confirmed?: boolean; caption: { text: string } };

/** 최대 개수 초과 시 확인한 오래된 항목부터 제거 */
export function capRemoteBacklog(items: RemoteBacklogItem[], max = MAX_ITEMS): RemoteBacklogItem[] {
  let out = items;
  while (out.length > max) {
    const i = out.findIndex((x) => x.confirmed);
    out = i >= 0 ? [...out.slice(0, i), ...out.slice(i + 1)] : out.slice(out.length - max);
  }
  return out;
}

const BACKLOG_KEY_PREFIX = 'cp-remote-backlog:';
const MAX_ITEMS = 50;

export function loadRemoteBacklog(kind: string): RemoteBacklogItem[] {
  try {
    const raw = window.sessionStorage.getItem(BACKLOG_KEY_PREFIX + kind);
    if (!raw) return [];
    const arr: unknown = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr.filter(
      (x): x is RemoteBacklogItem =>
        !!x &&
        typeof x.id === 'string' &&
        ((x.kind === 'notice' && !!x.notice && typeof x.notice.text === 'string') ||
          (x.kind === 'question' && !!x.question && typeof x.question.text === 'string') ||
          (x.kind === 'caption' && !!x.caption && typeof x.caption.text === 'string'))
    );
  } catch {
    return [];
  }
}

export function saveRemoteBacklog(kind: string, items: RemoteBacklogItem[]) {
  try {
    if (!items.length) window.sessionStorage.removeItem(BACKLOG_KEY_PREFIX + kind);
    else window.sessionStorage.setItem(BACKLOG_KEY_PREFIX + kind, JSON.stringify(capRemoteBacklog(items)));
  } catch {
    /* 저장 실패 무시 */
  }
}

/** 상단 앱 바(.cp-topnav, 36px) · 하단 슬라이드 네비(.cp-program-slide-footer-nav, 내용 높이) — 실제 크기 측정 */
const TOP_NAV_SELECTOR = '.cp-topnav';
const BOTTOM_NAV_SELECTOR = '.cp-program-slide-footer-nav';
const TOP_NAV_FALLBACK = 36;

type Insets = { top: number; bottom: number };

function visibleRects(sel: string): DOMRect[] {
  return Array.from(document.querySelectorAll(sel))
    .map((el) => el.getBoundingClientRect())
    .filter((r) => r.width > 2 && r.height > 2);
}

/** 레이어 좌표(페이지 zoom 보정) 기준 상·하단 네비 높이 */
export function useChromeInsets(layerRef: React.RefObject<HTMLElement | null>, active: boolean): Insets {
  const [ins, setIns] = useState<Insets>({ top: TOP_NAV_FALLBACK, bottom: 0 });
  useEffect(() => {
    if (!active) return undefined;
    const measure = () => {
      const layer = layerRef.current;
      if (!layer || !layer.clientWidth) return;
      const lr = layer.getBoundingClientRect();
      const scale = lr.width / layer.clientWidth || 1;
      const tops = visibleRects(TOP_NAV_SELECTOR).filter((r) => r.top - lr.top < 4);
      const top = tops.length ? Math.max(...tops.map((r) => (r.bottom - lr.top) / scale)) : 0;
      const bots = visibleRects(BOTTOM_NAV_SELECTOR).filter((r) => lr.bottom - r.bottom < 4);
      const bottom = bots.length ? Math.max(...bots.map((r) => (lr.bottom - r.top) / scale)) : 0;
      const next = { top: Math.max(0, top), bottom: Math.max(0, bottom) };
      setIns((p) => (Math.abs(p.top - next.top) < 0.5 && Math.abs(p.bottom - next.bottom) < 0.5 ? p : next));
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener(PAGE_ZOOM_CHANGED_EVENT, measure);
    const poll = window.setInterval(measure, 700);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener(PAGE_ZOOM_CHANGED_EVENT, measure);
      window.clearInterval(poll);
    };
  }, [layerRef, active]);
  return ins;
}

const Svg: React.FC<{ size?: number; children: React.ReactNode }> = ({ size = 16, children }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    {children}
  </svg>
);
export const BacklogIconCheck: React.FC<{ size?: number }> = ({ size }) => (
  <Svg size={size}>
    <path d="M5 12l5 5 9-10" />
  </Svg>
);
const IconUp = () => (
  <Svg>
    <path d="M6 15l6-6 6 6" />
  </Svg>
);
const IconClose = () => (
  <Svg>
    <path d="M6 6l12 12M18 6L6 18" />
  </Svg>
);
const IconQuestion = () => (
  <Svg size={14}>
    <circle cx="12" cy="12" r="9" />
    <path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .8-1 1.5V14" />
    <circle cx="12" cy="17" r=".6" fill="currentColor" />
  </Svg>
);
const IconNotice = () => (
  <Svg size={14}>
    <path d="M3 10v4h3l6 4V6L6 10z" />
    <path d="M16 9a4 4 0 0 1 0 6" />
  </Svg>
);
const IconClock = () => (
  <Svg size={12}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </Svg>
);
const IconInfo = () => (
  <Svg size={14}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v6M12 7.5v.5" />
  </Svg>
);

const timeLabel = (ms: number) => {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const stopKeys = (e: React.KeyboardEvent) => e.stopPropagation();

/**
 * 남은 메시지 줄(시안 S1): 어두운 배경 + 시계 노출 + 가로 스크롤 작은 카드.
 * collapsed 이면 시계 옆 작은 칩만 표시.
 */
export const RemoteMessageBacklog: React.FC<{
  items: RemoteBacklogItem[];
  collapsed: boolean;
  /** 확대 팝업이 위에 떠 있음 — 자동 접힘 중지 */
  popupOpen: boolean;
  onOpen: (item: RemoteBacklogItem) => void;
  onDismiss: (id: string) => void;
  onConfirmAll: () => void;
  onCollapse: () => void;
  onExpand: () => void;
}> = ({ items, collapsed, popupOpen, onOpen, onDismiss, onConfirmAll, onCollapse, onExpand }) => {
  const layerRef = useRef<HTMLDivElement | null>(null);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const insets = useChromeInsets(layerRef, items.length > 0);
  const navState = useRemoteBacklogNavState();
  const [fade, setFade] = useState({ l: false, r: false });

  const updFade = useCallback(() => {
    const s = scrollerRef.current;
    if (!s) return;
    const l = s.scrollLeft > 2;
    const r = s.scrollLeft < s.scrollWidth - s.clientWidth - 2;
    setFade((p) => (p.l === l && p.r === r ? p : { l, r }));
  }, []);

  /* 처음 펼칠 때·항목 수 변경 시 끝(최신)으로 */
  const count = items.length;
  useLayoutEffect(() => {
    if (collapsed) return;
    const s = scrollerRef.current;
    if (!s) return;
    s.scrollLeft = s.scrollWidth;
    updFade();
  }, [collapsed, count, updFade]);

  /* 세로 휠 → 가로 스크롤 */
  useEffect(() => {
    const s = scrollerRef.current;
    if (!s || collapsed) return undefined;
    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        s.scrollLeft += e.deltaY;
        e.preventDefault();
      }
    };
    s.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('resize', updFade);
    return () => {
      s.removeEventListener('wheel', onWheel);
      window.removeEventListener('resize', updFade);
    };
  }, [collapsed, count, updFade]);

  if (typeof document === 'undefined') return null;
  const unconfirmed = items.filter((x) => !x.confirmed).length;

  if (collapsed) {
    /* 상단 네비 버튼이 있으면 그쪽이 다시 열기 담당 */
    if (navState.navButtons > 0 || unconfirmed === 0) return null;
    const chipStyle: React.CSSProperties = { right: 20, top: insets.top + 12 };
    return createPortal(
      <div ref={layerRef} className="cp-remote-bl-chip-layer">
        <button
          type="button"
          className="cp-remote-bl-chip"
          style={chipStyle}
          onClick={onExpand}
          aria-label={`남은 메시지 ${unconfirmed}개 보기`}
        >
          <IconNotice />
          <span>남은 메시지</span>
          <span className="cp-remote-bl-cnt is-small">{unconfirmed}</span>
        </button>
      </div>,
      document.body
    );
  }

  return createPortal(
    // 줄 안의 키 입력은 페이지 단축키(슬라이드 넘김 등)로 전달하지 않음
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      ref={layerRef}
      className="cp-remote-bl-layer"
      onKeyDown={(e) => {
        stopKeys(e);
      }}
      onKeyUp={stopKeys}
      style={{ pointerEvents: 'none' }}
    >
      <div className="cp-remote-bl-dim" style={{ top: insets.top, bottom: insets.bottom }} aria-hidden />
      {/* 빈 곳 클릭은 아무 동작 없음(뒤 화면 클릭만 막음) — 접기는 '접기' 버튼으로만 */}
      <div className="cp-remote-bl-backdrop" style={{ top: insets.top, bottom: insets.bottom }} />
      <div
        className="cp-remote-bl-panel"
        style={{ top: insets.top + ((layerRef.current?.clientHeight ?? window.innerHeight) - insets.top - insets.bottom) / 2 }}
        role="region"
        aria-label="남은 메시지"
      >
        {count === 0 ? <p className="cp-remote-bl-empty">아직 받은 메시지가 없어요</p> : null}
        <div
          ref={scrollerRef}
          hidden={count === 0}
          role="group"
          aria-label="남은 메시지 목록 (가로 스크롤)"
          className={`cp-remote-bl-scroller${fade.l ? ' fl' : ''}${fade.r ? ' fr' : ''}`}
          onScroll={updFade}
        >
          <div className="cp-remote-bl-row">
            {items.map((it) => {
              const title = it.kind === 'question' ? it.question.text : it.kind === 'caption' ? it.caption.text : it.notice.text;
              let tagLabel = it.kind === 'caption' ? '자막' : '공지';
              if (it.kind === 'question') {
                const qt = it.question.questionType ?? 'short';
                tagLabel = QUESTION_TYPE_LABEL[qt];
              }
              const isQ = it.kind === 'question';
              const done = !!it.confirmed;
              return (
                <div
                  key={it.id}
                  role="button"
                  tabIndex={0}
                  className={`cp-remote-bl-card ${isQ ? 'q' : it.kind === 'caption' ? 'n c' : 'n'}${done ? ' is-confirmed' : ''}`}
                  onClick={() => onOpen(it)}
                  onKeyDown={(e) => {
                    if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      onOpen(it);
                    }
                  }}
                  aria-label={`${isQ ? '질문' : it.kind === 'caption' ? '자막' : '공지'}${done ? ' (확인함)' : ''}: ${title}`}
                >
                  <div className="cp-remote-bl-card-top">
                    <span className="cp-remote-bl-card-tags">
                      <span className="cp-remote-bl-tag">
                        {isQ ? <IconQuestion /> : <IconNotice />}
                        {tagLabel}
                      </span>
                      {!it.seen && !done ? <span className="cp-remote-bl-udot" aria-label="안 읽음" /> : null}
                    </span>
                    {done ? null : (
                    <button
                      type="button"
                      className="cp-remote-bl-ibtn"
                      aria-label="이 메시지 확인 처리"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDismiss(it.id);
                      }}
                    >
                      <IconClose />
                    </button>
                    )}
                  </div>
                  <div className="cp-remote-bl-ct">{title}</div>
                  <div className="cp-remote-bl-cm">
                    <span>{timeLabel(it.receivedAt)}</span>
                    {it.kind === 'question' && it.answered ? (
                        <span className="cp-remote-bl-st done">
                          <BacklogIconCheck size={12} />
                          답변 완료
                        </span>
                    ) : done ? (
                      <span className="cp-remote-bl-st seen">
                        <BacklogIconCheck size={12} />
                        확인함
                      </span>
                    ) : it.kind === 'question' ? (
                      it.answered ? (
                        <span className="cp-remote-bl-st done">
                          <BacklogIconCheck size={12} />
                          답변 완료
                        </span>
                      ) : (
                        <span className="cp-remote-bl-st wait">
                          <IconClock />
                          답변 대기
                        </span>
                      )
                    ) : (
                      <span className="cp-remote-bl-pre">확인 전</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {!popupOpen ? (
          <div className="cp-remote-bl-actions">
            {unconfirmed > 0 ? (
              <button type="button" className="cp-remote-bl-gbtn" onClick={onConfirmAll}>
                <BacklogIconCheck />
                모두 확인
              </button>
            ) : null}
            <button type="button" className="cp-remote-bl-gbtn is-narrow" onClick={onCollapse}>
              <IconUp />
              접기
            </button>
          </div>
        ) : null}
      </div>
      <div className="cp-remote-bl-hint" style={{ bottom: insets.bottom + 16 }}>
        <IconInfo />
        탭을 닫으면 이 목록은 사라집니다
      </div>
    </div>,
    document.body
  );
};
